/**
 * SOFSEK — Enterprise Risk Financial Heatmap (§12/D9/D10). Aggregates active
 * violations' bundle materiality against each monitored-org branch's risk
 * multiplier. All amounts are already expressed in the single corporate
 * currency (SFK.CORPORATE.CURRENCY) — no per-bundle currency, no fxr
 * conversion needed for this v1 screen.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import type { AuthSession } from '../../types/auth';
import { EmptyState } from '../../components/common/EmptyState';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { RefreshCw, Search } from 'lucide-react';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.RISK_HEATMAP.LIST';

interface HeatmapRow {
  org_node_id: number;
  org_internal_code: string;
  risk_multiplier: number;
  active_violation_count: number;
  exposure_amount: number;
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'org', header: 'Organizational Branch', defaultWidth: 240 },
  { key: 'risk_multiplier', header: 'Risk Multiplier', defaultWidth: 130 },
  { key: 'active_violation_count', header: 'Active Violations', defaultWidth: 150 },
  { key: 'exposure_amount', header: 'Exposure', defaultWidth: 180 },
];

function tlName(list: unknown, fallback: string): string {
  const arr = (list ?? []) as { locale_code: string; display_text: string }[];
  return arr.find(x => x.locale_code === 'EN_US')?.display_text ?? fallback;
}

function HeatmapInner() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<HeatmapRow[]>([]);
  const [filter, setFilter] = useState('');
  const [orgNames, setOrgNames] = useState<Record<number, string>>({});
  const [currency, setCurrency] = useState('USD');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [heatRes, orgRes, currRes] = await Promise.all([
        apiDb.from('sfk_v_risk_heatmap').select('org_node_id, org_internal_code, risk_multiplier, active_violation_count, exposure_amount').order('exposure_amount', { ascending: false }),
        apiDb.from('sfk_org_node').select('org_node_id, sfk_org_node_tl ( locale_code, display_text )'),
        apiDb.from('csm_system_parameters').select('parameter_value').eq('internal_code', 'SFK.CORPORATE.CURRENCY').eq('is_active', 'Y'),
      ]);
      if (heatRes.error) throw heatRes.error;
      if (orgRes.error) throw orgRes.error;
      setRows((heatRes.data ?? []) as unknown as HeatmapRow[]);
      const names: Record<number, string> = {};
      for (const r of (orgRes.data ?? []) as unknown as Record<string, unknown>[]) {
        names[r.org_node_id as number] = tlName(r.sfk_org_node_tl, String(r.org_node_id));
      }
      setOrgNames(names);
      const cur = (currRes.data ?? [])[0]?.parameter_value as string | undefined;
      if (cur) setCurrency(cur);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

   
  useEffect(() => { void reload(); }, []);

  const resolveNameForOrg = (r: HeatmapRow) => orgNames[r.org_node_id] ?? r.org_internal_code;
  const formatExposure = (amount: number) => `${currency} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const detail = useExpandedRows<number>();
  // §17.B13 — a branch's exposure breakdown; only branches carrying active violations expand.
  const buildRelatedGroups = (r: HeatmapRow): RelatedChildGroup[] => [
    { key: 'exposure', title: t('sfk.risk_heatmap.related.title', 'Exposure breakdown'), items: [
      { id: 'multiplier', label: `${t('sfk.risk_heatmap.col.multiplier', 'Risk Multiplier')}: ${r.risk_multiplier}` },
      { id: 'violations', label: `${t('sfk.risk_heatmap.col.violations', 'Active Violations')}: ${r.active_violation_count}` },
      { id: 'exposure', label: `${t('sfk.risk_heatmap.col.exposure', 'Exposure')}: ${formatExposure(r.exposure_amount)}` },
    ] },
  ];
  const hasRelated = (r: HeatmapRow) => r.active_violation_count > 0;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(r => !q || resolveNameForOrg(r).toLowerCase().includes(q));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filter, orgNames]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.risk_heatmap.title', 'Enterprise Risk Financial Heatmap')}
          description={t('sfk.risk_heatmap.subtitle', 'Active-violation exposure aggregated per organizational branch by risk multiplier.')}
          actions={
            <button onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
          }
        />
        <StatStrip stats={[{ value: rows.length, label: t('common.total', 'Total') }, { value: filtered.length, label: t('common.shown', 'Shown') }]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
            <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.org_node_id}>
                  <tr className={`hover:bg-csm-surface-alt ${r.exposure_amount > 0 ? 'bg-csm-danger-soft' : ''}`}>
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle">
                      <RelatedExpandButton open={detail.isOpen(r.org_node_id)} hasChildren={hasRelated(r)} onToggle={() => detail.toggle(r.org_node_id)}
                        expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'org' && resolveNameForOrg(r)}
                        {col.key === 'risk_multiplier' && r.risk_multiplier}
                        {col.key === 'active_violation_count' && r.active_violation_count}
                        {col.key === 'exposure_amount' && formatExposure(r.exposure_amount)}
                      </td>
                    ))}
                  </tr>
                  {detail.isOpen(r.org_node_id) && hasRelated(r) && (
                    <tr>
                      <td colSpan={visibleCols.length + 1} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-2">
                        <RelatedChildrenPanel groups={buildRelatedGroups(r)} emptyLabel={t('common.related.none', 'No related records')} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 1} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
            </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>
    </div>
  );
}

export function SfkRiskHeatmapPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <HeatmapInner />
    </PersonalizationProvider>
  );
}
